import React, { useCallback, useEffect, useMemo, useState } from 'react'
import UnauthLayout from '~/common/UnauthLayout'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import { useAuth } from '~/auth/Auth'
import { useQuery } from '@apollo/client'
import { GetSuggestedCommunitiesDocument, GetUserCommunitiesDocument } from '~/api/generated/graphql'
import { elementClickedNoEvent } from '~/common/EventLogger'
import RecommendedCommunityItem from '~/pages/feed/RecommendedCommunityItem'
import { Button } from 'react-bootstrap'
import { useNavigate } from 'react-router'

interface RegistrationCommunitiesProps {
  destination: string
}

export const RegistrationCommunities = ({ destination }: RegistrationCommunitiesProps) => {
  const { isCondensedPortrait } = useWindowSize()
  const { registeringAuthUserId: authUserId, loading: authLoading, setIsRegistering } = useAuth()
  const navigate = useNavigate()

  const [isButtonTransparent, setIsButtonTransparent] = useState<boolean>(true)
  const params = new URLSearchParams(destination.split('?')[1])
  const interest = params.get('i')

  const { data: recommendedData, loading: recommendedLoading } = useQuery(GetSuggestedCommunitiesDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { interest: interest },
  })
  const { data: userCommunityData, loading: userCommLoading } = useQuery(GetUserCommunitiesDocument, {
    variables: { id: authUserId ?? '' },
    skip: !authUserId,
    nextFetchPolicy: 'standby',
  })

  const joinedCommunityIds = useMemo(
    () =>
      (userCommunityData?.user?.memberships?.edges
        .filter(e => !e?.node?.community?.isHomepage)
        .map(e => e?.node?.communityId) ?? []) as string[],
    [userCommunityData]
  )

  const recommendedCommunityIds = useMemo(
    () =>
      recommendedData?.suggestedCommunities
        ?.filter(c => !c?.companyId)
        .map(c => ({
          communityId: c?.communityId ?? '',
          // randomly sort the communities, so they can be different each time
          sort: Math.random(),
        }))
        .sort((a, b) => a.sort - b.sort)
        .map(c => c.communityId)
        .filter(cId => !joinedCommunityIds?.includes(cId)),
    [recommendedData, joinedCommunityIds]
  )

  const loading =
    (authLoading && !authUserId) ||
    (userCommLoading && !userCommunityData) ||
    (recommendedLoading && !recommendedData) ||
    recommendedCommunityIds === undefined

  const joinedCommunitiesLength =
    // Ideally, we want to show 2 joined communities and 4 recommended communities. However, if there are less than
    // 4 recommended communities, we can show more joined communities. Similarly, if there are less than 2 joined
    // communities we can show more recommended communities. Either way, there should be no more than 6 total
    (recommendedCommunityIds?.length ?? 0) >= 4
      ? Math.min(2, joinedCommunityIds?.length ?? 0)
      : Math.min(6 - (recommendedCommunityIds?.length ?? 0), 4)
  const recommendCommunitiesLength = 6 - joinedCommunitiesLength

  const onContinue = useCallback(() => {
    setIsRegistering(false)
    void navigate(destination, { state: { fromLoginRedirect: true }, replace: true })
  }, [destination, navigate, setIsRegistering])

  // Skip page if there are no recommended communities
  useEffect(() => {
    if (!loading && !authLoading && recommendedCommunityIds?.length === 0) {
      onContinue()
    }
  }, [loading, authLoading, recommendedCommunityIds, onContinue])

  // Timer for button to become prominently styled
  useEffect(() => {
    const timeout = setTimeout(() => setIsButtonTransparent(false), 30_000)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    if (!loading && recommendedCommunityIds?.length > 0) {
      elementClickedNoEvent('show-registration-recommended-communities', { recommendedCommunityIds })
    }
  }, [loading, recommendedCommunityIds])

  return (
    <UnauthLayout className={'create-profile'} md={9} lg={8} xl={7} xxl={6}>
      <div className={'dialog registration-communities'}>
        <div className={'dialog-content'}>
          <div className={'dialog-head'} />
          <div className={'registration-recommended-container'}>
            <h2>Let's get you in with your people:</h2>
            {loading || authLoading ? (
              <>Loading suggested communities...</>
            ) : (
              recommendedCommunityIds?.length && (
                <>
                  <p className={'join-explanation'}>
                    <i>Join communities to get notifications of new roadmaps, release information, and more</i>
                  </p>
                  <div className={`communities-list ${isCondensedPortrait ? 'condensed' : ''}`}>
                    <hr />
                    {joinedCommunityIds?.slice(0, joinedCommunitiesLength).map(communityId => (
                      <>
                        <RecommendedCommunityItem
                          key={communityId}
                          communityId={communityId}
                          showOverlay={false}
                          linkToCommunity={false}
                        />
                        <hr />
                      </>
                    ))}
                    {recommendedCommunityIds?.slice(0, recommendCommunitiesLength).map(cId => (
                      <>
                        <RecommendedCommunityItem
                          key={cId}
                          communityId={cId}
                          showOverlay={false}
                          linkToCommunity={false}
                        />
                        <hr />
                      </>
                    ))}
                  </div>
                  {
                    <Button
                      className={`modal-continue ${isButtonTransparent ? 'transparent' : ''}`}
                      onClick={onContinue}
                    >
                      {`Continue >`}
                    </Button>
                  }
                </>
              )
            )}
          </div>
        </div>
      </div>
    </UnauthLayout>
  )
}
